/*
 * Gallery Section – Showcase real photos of Sharon in action
 * Masonry-style grid with hover effects
 * Uses authentic client photos from Sharon's studio and training sessions
 */

import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { X } from "lucide-react";

const galleryImages = [
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/WORKINGWITHTHEOLDPEOPLE1_3786b382.jpeg",
    alt: "אימון גיל השלישי בסטודיו בשוהם - מאמנת כושר שרון גולדברג",
    span: "",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/OLDLADYWORKOUTATHOMEPRIVATE_c1c600b4.jpeg",
    alt: "אימון פונקציונלי פרטי בבית בשוהם - כושר לגיל השלישי",
    span: "",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/OLDCOUPLEPRIVATEWORKOUT_c5b9185f.jpeg",
    alt: "אימון פרטי לזוג בבית בשוהם - אימון פונקציונלי",
    span: "",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/SHARONTRAININGONHERHOME_d4c04028.webp",
    alt: "שרון מלמדת פילאטיס מכשירים בבית בשוהם",
    span: "",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/SHARONWITHHEROLDcostumer_b3bc44cd.jpeg",
    alt: "שרון עם לקוחה מבוגרת - אימון גיל השלישי בשוהם",
    span: "md:col-span-1",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/sharonliftingtrainingball_b25a1310.jpeg",
    alt: "שרון מרימה כדור אימון בסטודיו בשוהם",
    span: "",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/אימוניםאישייםבביתהלקוח_e2db2891.jpeg",
    alt: "אימון פונקציונלי בבית בשוהם - ציוד אימון פרטי",
    span: "",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/אימוןפונקציונליללקוחות_fe0cc703.jpeg",
    alt: "אימון פונקציונלי בשוהם - אימון בחוץ עם רצועות התנגדות",
    span: "md:col-span-2",
    height: "h-64 md:h-56",
  },
  {
    src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/sharontrainingonstudio_0aa6d7e2.jpeg",
    alt: "שרון מלמדת פילאטיס מכשירים בסטודיו בשוהם",
    span: "",
    height: "h-64 md:h-56",
  },
];

export default function GallerySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [lightbox, setLightbox] = useState<string | null>(null);

  return (
    <section id="gallery" className="relative py-24 md:py-36 bg-[#FFF8F0]">
      <div className="container relative z-10" ref={ref}>
        {/* Section title */}
        <motion.div
          initial={false}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className="text-[#E8A87C] text-sm font-semibold tracking-wider uppercase mb-3 block"
            style={{ fontFamily: "var(--font-body)" }}
          >
            בואו נראה
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B4332]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            גלריה
          </h2>
          <div className="w-20 h-1 bg-[#E8A87C] mx-auto mt-6 rounded-full" />
        </motion.div>

        {/* Gallery grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 auto-rows-max">
          {galleryImages.map((img, i) => (
            <motion.div
              key={img.src}
              initial={false}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1 + i * 0.1, duration: 0.5 }}
              className={`${img.span} ${img.height} overflow-hidden rounded-2xl cursor-pointer group relative shadow-md hover:shadow-lg transition-all duration-300`}
              onClick={() => setLightbox(img.src)}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white hover:text-[#E8A87C] transition-colors"
            aria-label="סגור"
          >
            <X size={32} />
          </button>
          <img
            src={lightbox}
            alt="אימון פילאטיס ופונקציונלי בשוהם - שרון גולדברג"
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
